import { GIFTCARD_UNAVAILABLE_VALUE } from 'constants/appConstants';
import {
  RECEIVE_ACCOUNT_INFO,
  RECEIVE_GIFT_CARD_BALANCE,
  RECEIVE_REDEEM_GIFT_CARD_FAILURE,
  RECEIVE_REDEEM_GIFT_CARD_SUCCESS,
  RECEIVE_WISHES,
  REMOVE_ITEM_FOR_CANCELLATION_OR_RETURN,
  REQUEST_ACCOUNT_INFO,
  REQUEST_GIFT_CARD_BALANCE,
  REQUEST_REDEEM_GIFT_CARD,
  RESET_CANCEL_OR_RETURN_ITEMS,
  SELECT_ITEM_FOR_CANCELLATION_OR_RETURN,
  SET_ITEM_NOTIFICATION,
  UPDATE_GIFT_CARD_REDEEM_CODE
} from 'constants/reduxActions';
import { CONSTRAINT_VIOLATIONS_MESSAGES } from 'constants/constraintViolations';

import { authenticationErrorCatchHandlerFactory, err, setError } from 'actions/errors';
import { claimGiftCard, getCustomer, getGiftCard, getWishes, logoutCustomer, saveWish, subscribeToStockNotification } from 'apis/mafia';
import { ERROR_NOT_AUTHENTICATED, fetchApiNotAuthenticatedMiddleware, fetchErrorMiddleware, fetchErrorMiddlewareAllowAllErrorsWithServerLogging } from 'middleware/fetchErrorMiddleware';
import { prependAppRoot } from 'history/AppRootUtils';
import { setSessionCookies } from 'actions/session';
import { processHeadersMiddleware } from 'middleware/processHeadersMiddlewareFactory';

export function requestAccountInfo() {
  return {
    type: REQUEST_ACCOUNT_INFO
  };
}

export function receiveAccountInfo(customerInfo) {
  return {
    type: RECEIVE_ACCOUNT_INFO,
    customerInfo
  };
}

export function receiveWishes(wishes) {
  return {
    type: RECEIVE_WISHES,
    wishes
  };
}

export function requestGiftCardBalance() {
  return {
    type: REQUEST_GIFT_CARD_BALANCE
  };
}

export function receiveGiftCardBalance(giftCardBalance) {
  return {
    type: RECEIVE_GIFT_CARD_BALANCE,
    giftCardBalance
  };
}

export function requestRedeemGiftCard() {
  return {
    type: REQUEST_REDEEM_GIFT_CARD
  };
}

export function receiveRedeemGiftCardSuccess(giftCardBalance) {
  return {
    type: RECEIVE_REDEEM_GIFT_CARD_SUCCESS,
    giftCardBalance
  };
}

export function receiveRedeemGiftCardFailure(giftCardRedeemError) {
  return {
    type: RECEIVE_REDEEM_GIFT_CARD_FAILURE,
    giftCardRedeemError
  };
}

export function updateGiftCardRedeemCode(giftCardRedeemCode) {
  return {
    type: UPDATE_GIFT_CARD_REDEEM_CODE,
    giftCardRedeemCode
  };
}

// this does not support multiple EGCs, consider using lineItemId if we eventually support
export function setItemNotification(idArr) {
  const id = idArr[0];
  return {
    type: SET_ITEM_NOTIFICATION,
    id
  };
}

export function selectItemForCancellationOrReturn(item) {
  return {
    type: SELECT_ITEM_FOR_CANCELLATION_OR_RETURN,
    item
  };
}

export function removeItemForCancellationOrReturn(item) {
  return {
    type: REMOVE_ITEM_FOR_CANCELLATION_OR_RETURN,
    item
  };
}

export const resetCancelOrReturnItems = () => ({
  type: RESET_CANCEL_OR_RETURN_ITEMS
});

export function fetchAccountInfo(returnTo) {
  return (dispatch, getState) => {
    dispatch(requestAccountInfo());
    const { cookies, environmentConfig: { api: { mafia } }, routing: { locationBeforeTransitions } } = getState();
    return getCustomer(mafia, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then(({ customerInfo }) => {
        dispatch(receiveAccountInfo(customerInfo));
      })
      .catch(e => {
        // Optional auth error handler since some pages don't have any other API calls but still require authentication
        if (returnTo) {
          return authenticationErrorCatchHandlerFactory(dispatch, prependAppRoot(returnTo, locationBeforeTransitions))(e);
        }
        // ignore auth errors here, because at the time of writing this call is always used in conjunction with a more specific
        // fetch call that should do the redirecting.
        if (e.id !== ERROR_NOT_AUTHENTICATED) {
          dispatch(setError(err.GENERIC, e));
        }
      });
  };
}

export function fetchGiftCardBalance() {
  return (dispatch, getState) => {
    dispatch(requestGiftCardBalance());
    const { cookies, environmentConfig: { api: { mafia } }, routing: { locationBeforeTransitions } } = getState();
    return getGiftCard(mafia, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddlewareAllowAllErrorsWithServerLogging('API-myAccount-giftCard'))
      .then(({ balance = GIFTCARD_UNAVAILABLE_VALUE }) => { // if giftcards are enabled and getting a 400 or 404 default to unavailable
        dispatch(receiveGiftCardBalance(balance));
      })
      .catch(authenticationErrorCatchHandlerFactory(dispatch, prependAppRoot('/account', locationBeforeTransitions)));
  };
}

export function fetchWishes() {
  return (dispatch, getState) => {
    const { cookies, environmentConfig: { api: { mafia } }, routing: { locationBeforeTransitions } } = getState();
    return getWishes(mafia, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then(resp => {
        dispatch(receiveWishes(resp?.wishes));
      })
      .catch(authenticationErrorCatchHandlerFactory(dispatch, prependAppRoot('/account', locationBeforeTransitions)));
  };
}

export function storeWish({ category, text }) {
  return (dispatch, getState) => {
    const { cookies, environmentConfig: { api: { mafia } }, routing: { locationBeforeTransitions } } = getState();
    return saveWish(mafia, cookies, { category, text })
      .then(processHeadersMiddleware(setSessionCookies(dispatch)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(resp => {
        if (resp?.status === 400) {
          alert('Your wish is invalid. Please try something else.');
        } else {
          dispatch(fetchWishes());
        }
      })
      .catch(authenticationErrorCatchHandlerFactory(dispatch, prependAppRoot('/account', locationBeforeTransitions)));
  };
}
export function fireProductNotifyMe(data, apiNotifyMeSubscription = subscribeToStockNotification) {
  return function (dispatch, getState) {
    const { cookies, environmentConfig: { api: { mafia } } } = getState();

    return apiNotifyMeSubscription(mafia, data, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch)))
      .then(fetchErrorMiddleware)
      .then(() => {
        const id = Object.values(data)[0];
        dispatch(setItemNotification(id));
      })
      .catch(e => dispatch(setError(err.CART,e)));
  };
}

export function redeemGiftCard(giftCardRedeemCode, element) {
  return (dispatch, getState) => {
    dispatch(requestRedeemGiftCard());
    const { cookies, environmentConfig: { api: { mafia } }, routing: { locationBeforeTransitions } } = getState();
    return claimGiftCard(mafia, giftCardRedeemCode, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then(({ balance, cvs }) => {
        const { account: { giftCard } } = CONSTRAINT_VIOLATIONS_MESSAGES;

        if (cvs.length) {
          // Display gift card failure error message
          const giftCardRedeemError = giftCard[cvs[0].name];
          dispatch(receiveRedeemGiftCardFailure(giftCardRedeemError));
        } else {
          // Update gift card balance
          dispatch(receiveRedeemGiftCardSuccess(balance));
        }

        // Focus gift card redeem input field
        element.focus();
      })
      .catch(authenticationErrorCatchHandlerFactory(dispatch, prependAppRoot('/account', locationBeforeTransitions)));
  };
}

export function logoutAsCustomer() {
  return (dispatch, getState) => {
    const { cookies, environmentConfig: { api: { mafia } } } = getState();
    return logoutCustomer(mafia, cookies);
  };
}
